import { useEffect, useState, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import {
  Plus, Pencil, Trash2, Check, Circle, GripVertical,
  MessageCircle, Image as ImageIcon, Search, Settings, Zap,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ko';
dayjs.extend(relativeTime);
dayjs.locale('ko');

const DEPT_LABELS = {
  product: '상품개발본부',
  material: '소재개발본부',
  equipment: '장비개발본부',
};

const DEPT_BASE = {
  product: '/work/product/boards',
  material: '/work/material/boards',
  equipment: '/work/equipment/boards',
};

export default function BoardsDeptPage({ department }) {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');
  const isSuperadmin = user.role === 'superadmin';
  const basePath = DEPT_BASE[department];

  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(null); // null = 로딩 전, 'all' = 전체
  const [tasks, setTasks] = useState([]);
  const [taskTotal, setTaskTotal] = useState(0);
  const [taskPage, setTaskPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [showBoardForm, setShowBoardForm] = useState(false);
  const [showBoardPicker, setShowBoardPicker] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [boardForm, setBoardForm] = useState({ name: '', description: '', sortOrder: 0 });
  const dragBoardRef = useRef(null);
  const tasksRef = useRef([]);
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  const loadBoards = () =>
    api.get('/boards', { params: { department } }).then(r => {
      setBoards(r.data);
      if (r.data.length > 0 && !selectedBoardId) {
        setSelectedBoardId('all');
      }
    });

  const loadTasks = useCallback(() => {
    if (!selectedBoardId) return;
    if (selectedBoardId === 'all') {
      api.get('/boards/all-tasks', {
        params: { department, page: taskPage, limit: 50, search },
      }).then(r => {
        setTasks(r.data.tasks || []);
        setTaskTotal(r.data.total || 0);
      });
    } else {
      api.get(`/boards/${selectedBoardId}/tasks`, {
        params: { page: taskPage, limit: 20, search },
      }).then(r => {
        setTasks(r.data.tasks || []);
        setTaskTotal(r.data.total || 0);
      });
    }
  }, [selectedBoardId, taskPage, search, department]);

  useEffect(() => { loadBoards(); }, [department]);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  const toggle = async (taskId, boardId) => {
    const bid = boardId || selectedBoardId;
    await api.patch(`/boards/${bid}/tasks/${taskId}/toggle`);
    loadTasks();
  };

  const removeTask = async (taskId, boardId) => {
    if (!confirm('삭제하시겠습니까?')) return;
    const bid = boardId || selectedBoardId;
    await api.delete(`/boards/${bid}/tasks/${taskId}`);
    loadTasks();
  };

  const createTask = async (boardId) => {
    const targetId = boardId || selectedBoardId;
    if (!targetId || targetId === 'all') return;
    try {
      const r = await api.post(`/boards/${targetId}/tasks`, { title: '새 할일', content: '' });
      setShowBoardPicker(false);
      navigate(`${basePath}/${targetId}/tasks/${r.data.id}`);
    } catch (e) {
      alert('할일 생성 실패: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleAddClick = () => {
    if (isAllTab) {
      setShowBoardPicker(true);
    } else {
      createTask();
    }
  };


const saveBoard = async () => {
    if (!boardForm.name.trim()) return;
    if (editingBoard) {
      await api.put(`/boards/${editingBoard.id}`, boardForm);
    } else {
      await api.post('/boards', { ...boardForm, department });
    }
    setShowBoardForm(false);
    await loadBoards();
  };

  const removeBoard = async (id) => {
    if (!confirm('게시판을 삭제하시겠습니까?\n(할일이 있으면 삭제되지 않을 수 있습니다)')) return;
    await api.delete(`/boards/${id}`);
    if (selectedBoardId === id) setSelectedBoardId('all');
    await loadBoards();
  };

  const onBoardDragStart = (e, idx) => {
    e.dataTransfer.setData('text/plain', String(idx));
    e.dataTransfer.effectAllowed = 'move';
    dragBoardRef.current = idx;
  };
  const onBoardDrop = async (e, toIdx) => {
    e.preventDefault();
    const fromIdx = dragBoardRef.current;
    dragBoardRef.current = null;
    if (fromIdx === null || fromIdx === toIdx) return;
    const newBoards = [...boards];
    const [moved] = newBoards.splice(fromIdx, 1);
    newBoards.splice(toIdx, 0, moved);
    setBoards(newBoards);
    await api.patch('/boards/reorder', {
      orders: newBoards.map((b, i) => ({ id: b.id, sortOrder: i })),
    });
    loadBoards();
  };

  const onTaskDragStart = (e, idx) => {
    e.dataTransfer.setData('text/plain', String(idx));
    e.dataTransfer.effectAllowed = 'move';
  };
  const onTaskDrop = async (e, toIdx) => {
    e.preventDefault();
    e.stopPropagation();
    const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (isNaN(fromIdx) || fromIdx === toIdx) return;
    const cur = tasksRef.current;
    const arr = [...cur];
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, moved);
    flushSync(() => setTasks(arr));
    try {
      await api.patch(`/boards/${selectedBoardId}/tasks/reorder`, {
        orders: arr.map((t, i) => ({ id: t.id, sortOrder: i })),
      });
    } catch {
      setTasks(cur);
    }
  };

  const selectTab = (id) => {
    setSelectedBoardId(id);
    setTaskPage(1);
    setSearch('');
    setSearchInput('');
  };

  const doSearch = () => { setTaskPage(1); setSearch(searchInput); };
  const totalPages = Math.ceil(taskTotal / (selectedBoardId === 'all' ? 50 : 20));
  const selectedBoard = boards.find(b => b.id === selectedBoardId);
  const isAllTab = selectedBoardId === 'all';

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 탭 헤더 */}
      <div className="flex items-center justify-between mb-0 flex-shrink-0">
        <div className="flex items-end gap-0 overflow-x-auto scrollbar-none">
          {/* 전체 탭 */}
          <button
            onClick={() => selectTab('all')}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex-shrink-0 ${
              isAllTab
                ? 'text-brand border-brand'
                : 'text-gray-400 border-transparent hover:text-white hover:border-gray-600'
            }`}
          >
            전체
            {boards.reduce((sum, b) => sum + (b.pendingCount || 0), 0) > 0 && (
              <span className="ml-1.5 bg-brand/20 text-brand text-[10px] px-1.5 py-0.5 rounded-full">
                {boards.reduce((sum, b) => sum + (b.pendingCount || 0), 0)}
              </span>
            )}
          </button>

          {boards.map((b, idx) => (
            <button
              key={b.id}
              onClick={() => selectTab(b.id)}
              draggable={isSuperadmin}
              onDragStart={isSuperadmin ? e => onBoardDragStart(e, idx) : undefined}
              onDragOver={isSuperadmin ? e => e.preventDefault() : undefined}
              onDrop={isSuperadmin ? e => onBoardDrop(e, idx) : undefined}
              title={isSuperadmin ? '드래그로 순서 변경' : undefined}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex-shrink-0 ${
                isSuperadmin ? 'cursor-grab active:cursor-grabbing' : ''
              } ${
                selectedBoardId === b.id
                  ? 'text-brand border-brand'
                  : 'text-gray-400 border-transparent hover:text-white hover:border-gray-600'
              }`}
            >
              {b.name}
              {b.pendingCount > 0 && (
                <span className="ml-1.5 bg-brand/20 text-brand text-[10px] px-1.5 py-0.5 rounded-full">
                  {b.pendingCount}
                </span>
              )}
            </button>
          ))}
          {isSuperadmin && (
            <button
              onClick={() => { setEditingBoard(null); setBoardForm({ name: '', description: '', sortOrder: 0 }); setShowBoardForm(true); }}
              className="px-3 py-2.5 text-gray-600 hover:text-brand border-b-2 border-transparent transition-colors flex-shrink-0"
              title="게시판 추가"
            >
              <Plus size={15} />
            </button>
          )}
        </div>

        {selectedBoard && isSuperadmin && (
          <div className="flex items-center gap-1 pb-1 flex-shrink-0">
            <button
              onClick={() => { setEditingBoard(selectedBoard); setBoardForm({ name: selectedBoard.name, description: selectedBoard.description || '', sortOrder: selectedBoard.sortOrder }); setShowBoardForm(true); }}
              className="p-1.5 text-gray-600 hover:text-white rounded hover:bg-gray-800"
            >
              <Settings size={13} />
            </button>
          </div>
        )}
      </div>

      <div className="border-b border-gray-800 mb-4 flex-shrink-0" />

      {/* 검색 + 추가 버튼 */}
      <div className="flex gap-2 mb-4 flex-shrink-0">
        <div className="flex-1 flex items-center bg-gray-900 border border-gray-800 rounded-lg px-3 min-w-0">
          <Search size={14} className="text-gray-500 flex-shrink-0" />
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
            placeholder="제목 검색..."
            className="flex-1 bg-transparent text-white text-sm py-2 px-2 focus:outline-none min-w-0"
          />
          <button onClick={doSearch} className="flex-shrink-0 px-3 py-1 bg-gray-700 text-gray-300 text-sm rounded-md hover:bg-gray-600">검색</button>
        </div>
        <button
          onClick={handleAddClick}
          className="flex items-center gap-1.5 bg-brand text-white text-sm px-4 py-2 rounded-lg hover:bg-brand/80 flex-shrink-0"
        >
          <Plus size={16} /> 추가
        </button>
      </div>

      {/* 할일 목록 */}
      <div className="flex-1 overflow-y-auto">
        {tasks.length === 0 ? (
          <div className="text-center py-16 text-gray-600 text-sm">
            {search ? '검색 결과가 없습니다.' : '할일이 없습니다.'}
          </div>
        ) : (
          <div className="space-y-1.5">
            {tasks.map((t, idx) => (
              <div
                key={t.id}
                draggable={!isAllTab && isSuperadmin}
                onDragStart={!isAllTab && isSuperadmin ? e => onTaskDragStart(e, idx) : undefined}
                onDragOver={!isAllTab && isSuperadmin ? e => e.preventDefault() : undefined}
                onDrop={!isAllTab && isSuperadmin ? e => onTaskDrop(e, idx) : undefined}
                className={`flex items-center gap-3 bg-gray-900 border rounded-lg px-4 py-3 group transition-all ${
                  t.completed ? 'border-gray-800/50 opacity-50' : 'border-gray-800 hover:border-gray-700'
                } ${!isAllTab && isSuperadmin ? 'cursor-grab active:cursor-grabbing' : ''}`}
              >
                {!isAllTab && isSuperadmin && (
                  <GripVertical size={14} className="text-gray-600 flex-shrink-0 opacity-0 group-hover:opacity-100" />
                )}
                <button onClick={() => toggle(t.id, t.board?.id)} className="flex-shrink-0">
                  {t.completed
                    ? <Check size={18} className="text-green-400" />
                    : <Circle size={18} className="text-gray-600 hover:text-brand" />}
                </button>
                <div
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => navigate(`${basePath}/${t.boardId || selectedBoardId}/tasks/${t.id}`)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isAllTab && t.board && (
                      <span className="flex-shrink-0 text-[10px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">
                        {t.board.name}
                      </span>
                    )}
                    <p className={`text-sm font-medium truncate ${t.completed ? 'text-gray-500 line-through' : 'text-white'}`}>
                      {t.title}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                    <span>{t.author?.name || t.author?.username}</span>
                    <span>{dayjs(t.createdAt).format('MM.DD HH:mm')}</span>
                    {t.images?.length > 0 && <span className="flex items-center gap-0.5"><ImageIcon size={10} />{t.images.length}</span>}
                    {t.comments?.length > 0 && <span className="flex items-center gap-0.5"><MessageCircle size={10} />{t.comments.length}</span>}
                    {t.completed && t.completer && <span className="text-green-400/60">{t.completer.name} 완료</span>}
                  </div>
                </div>
                {/* 최근 수정 정보 - updatedAt이 createdAt보다 2초 이상 이후면 표시 */}
                {new Date(t.updatedAt) - new Date(t.createdAt) > 2000 && (
                  <div className="flex-shrink-0 flex flex-col items-end gap-0.5 min-w-[72px]">
                    <div className="flex items-center gap-1">
                      {Date.now() - new Date(t.updatedAt).getTime() < 86400000 && (
                        <Zap size={10} className="text-brand flex-shrink-0" />
                      )}
                      {t.updater && (
                        <span className="text-[10px] text-gray-500 truncate max-w-[64px]">{t.updater.name}</span>
                      )}
                    </div>
                    <span className="text-[10px] text-gray-600">{dayjs(t.updatedAt).fromNow()}</span>
                  </div>
                )}
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  {(t.authorId === user.id || isSuperadmin) && (
                    <button onClick={() => removeTask(t.id, t.board?.id)} className="p-1 text-gray-500 hover:text-red-400">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => Math.abs(p - taskPage) <= 2 || p === 1 || p === totalPages)
              .map((p, i, arr) => (
                <span key={p}>
                  {i > 0 && arr[i - 1] !== p - 1 && <span className="text-gray-600 px-1">…</span>}
                  <button
                    onClick={() => setTaskPage(p)}
                    className={`w-8 h-8 rounded-lg text-sm ${p === taskPage ? 'bg-brand text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
                  >
                    {p}
                  </button>
                </span>
              ))}
          </div>
        )}
      </div>

      {/* 전체 탭에서 추가 시 게시판 선택 */}
      {showBoardPicker && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowBoardPicker(false)}>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-xs" onClick={e => e.stopPropagation()}>
            <h2 className="text-white font-bold text-sm mb-4">추가할 게시판 선택</h2>
            <div className="space-y-2">
              {boards.map(b => (
                <button key={b.id} onClick={() => createTask(b.id)}
                  className="w-full text-left px-3 py-2.5 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg flex items-center justify-between transition-colors">
                  <span>{b.name}</span>
                  <Plus size={14} className="text-brand flex-shrink-0" />
                </button>
              ))}
            </div>
            <button onClick={() => setShowBoardPicker(false)} className="mt-4 w-full px-3 py-2 text-gray-500 text-sm hover:text-white">취소</button>
          </div>
        </div>
      )}

      {/* 게시판 추가/수정 모달 */}
      {showBoardForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowBoardForm(false)}>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold">{editingBoard ? '게시판 수정' : '게시판 추가'}</h2>
              {editingBoard && (
                <button onClick={() => removeBoard(editingBoard.id)} className="text-gray-600 hover:text-red-400 text-xs flex items-center gap-1">
                  <Trash2 size={12} /> 삭제
                </button>
              )}
            </div>
            <input
              value={boardForm.name}
              onChange={e => setBoardForm(f => ({ ...f, name: e.target.value }))}
              placeholder="게시판 이름"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm mb-3 focus:outline-none focus:border-brand"
            />
            <input
              value={boardForm.description}
              onChange={e => setBoardForm(f => ({ ...f, description: e.target.value }))}
              placeholder="설명 (선택)"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm mb-4 focus:outline-none focus:border-brand"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowBoardForm(false)} className="px-4 py-2 text-gray-400 text-sm hover:text-white">취소</button>
              <button onClick={saveBoard} className="px-4 py-2 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">저장</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
